import { CALENDAR_ROLES } from '../../features/calendar/calendar-access';
import { IconName } from '../icon/icon.component';

/** What the rules read of the signed-in user (the sidebar's and the header's own auth service, or a plain object in a spec). */
export interface ShortcutAuth {
  hasRole(role: string): boolean;
  isInOrganizationVendorOnly(): boolean;
}

/** The roles that approve by role, and so get the approvals shortcut (and the count behind it); a tenant, technician or vendor does not, however they were routed. */
export const APPROVER_ROLES: readonly string[] = ['FacilityManager', 'SystemAdmin', 'StoreInventoryManager', 'AssetManager'];

/** Every role that can be assigned a job task (the provider's technician roles, docs/design/34 section 14.10.8.1). */
export const TASK_ROLES: readonly string[] = ['MaintenanceTechnician', 'VendorContractor'];

export type ShortcutId = 'approvals' | 'tasks' | 'calendar';

export interface ShortcutDef {
  id: ShortcutId;
  path: string;
  labelKey: string;
  icon: IconName;
}

/** The three personal shortcuts that used to be the "My work" group of the sidebar: icon buttons in the header (in the user menu on a phone). */
export const SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'approvals', path: '/my-approvals', labelKey: 'shared.nav.myApprovals', icon: 'shield-check' },
  { id: 'tasks', path: '/my-tasks', labelKey: 'shared.nav.myTasks', icon: 'clipboard-list' },
  { id: 'calendar', path: '/calendar', labelKey: 'shared.nav.calendar', icon: 'calendar' }
];

/** Whether the user holds at least one approver role (a mixed user, a tenant who is also a facility manager, does). */
export const canSeeApprovals = (auth: Pick<ShortcutAuth, 'hasRole'>): boolean => APPROVER_ROLES.some((role) => auth.hasRole(role));

/** Where the My Tasks nav item was shown, except for a user whose home is My Tasks (the technician-only user): it would only repeat Home. */
export const canSeeTasks = (auth: ShortcutAuth, homeRoute: string): boolean =>
  TASK_ROLES.some((role) => auth.hasRole(role)) && !auth.isInOrganizationVendorOnly() && homeRoute !== '/my-tasks';

/** Where the Calendar nav item was shown. */
export const canSeeCalendar = (auth: Pick<ShortcutAuth, 'hasRole'>): boolean => CALENDAR_ROLES.some((role) => auth.hasRole(role));

/** The shortcuts the user gets, in order. */
export function visibleShortcuts(auth: ShortcutAuth, homeRoute: string): ShortcutDef[] {
  const allowed: Record<ShortcutId, boolean> = {
    approvals: canSeeApprovals(auth),
    tasks: canSeeTasks(auth, homeRoute),
    calendar: canSeeCalendar(auth)
  };
  return SHORTCUTS.filter((s) => allowed[s.id]);
}
